import { useSnackbar } from "notistack"
import * as storageApi from "../api"
import { unknownErrorToString } from "../../utils/errors"
import { useTranslation } from "react-i18next"

export const useFileMutations = () => {
  const translations = useTranslations()

  const { enqueueSnackbar } = useSnackbar()

  const handleError = (error: any) => {
    const errorMessage = unknownErrorToString(error)

    enqueueSnackbar(errorMessage, {
      variant: "error",
    })
  }

  const [deleteStorageFileMutation] = storageApi.useDeleteStorageFileMutation()

  const [generatePresignedUploadUrlsMutation] = storageApi.useGeneratePresignedUploadUrlsMutation()

  const [confirmUploadMutation] = storageApi.useConfirmUploadMutation()

  const [attachFilesMutation] = storageApi.useAttachFilesMutation()

  const deleteFile = async (fileId: string) => {
    return deleteStorageFileMutation({
      fileId,
    })
      .unwrap()
      .then(() => {
        enqueueSnackbar(translations.fileDeletionSuccessful, {
          variant: "success",
        })
      })
      .catch(() =>
        handleError({
          message: translations.fileDeletionFailed,
        })
      )
  }

  const uploadFiles = async ({ files, entityId, entityType, orgId }: UploadFilesParams) => {
    try {
      const presignedResponse = await generatePresignedUploadUrlsMutation({
        files: files.map((file) => ({
          filename: file.name,
          contentType: file.type,
        })),
        orgId,
      }).unwrap()

      const uploadedKeys: string[] = []

      await Promise.all(
        presignedResponse.files.map(async (presignedFile, index) => {
          const file = files[index]

          await fetch(presignedFile.uploadUrl, {
            method: "PUT",
            headers: {
              "Content-Type": file.type,
            },
            body: file,
          })

          await confirmUploadMutation({
            key: presignedFile.key,
          }).unwrap()

          uploadedKeys.push(presignedFile.key)
        })
      )

      await attachFilesMutation({
        keys: uploadedKeys,
        entityId,
        entityType,
      }).unwrap()

      enqueueSnackbar(translations.fileUploadSuccessful, {
        variant: "success",
      })

      return true
    } catch (error) {
      handleError({
        message: translations.fileUploadFailed,
      })

      return false
    }
  }

  return {
    uploadFiles,
    deleteFile,
  }
}

type UploadFilesParams = {
  files: File[]

  entityId: string

  entityType: "project" | "task"

  orgId?: string
}

const defaultTranslations = {
  fileUploadSuccessful: "Files uploaded successfully",
  fileUploadFailed: "Failed to upload files",
  fileDeletionSuccessful: "File deleted successfully",
  fileDeletionFailed: "Failed to delete file",
}

const useTranslations = (defaults = defaultTranslations) => {
  const { t } = useTranslation("storage-file")

  return {
    fileUploadSuccessful: t("fileUploadSuccess", defaults.fileUploadSuccessful),
    fileUploadFailed: t("fileUploadError", defaults.fileUploadFailed),
    fileDeletionSuccessful: t("fileDeleteSuccess", defaults.fileDeletionSuccessful),
    fileDeletionFailed: t("fileDeleteError", defaults.fileDeletionFailed),
  }
}
